// SettingsScreen (§10) — Preferences (sound/music/hints), Theme (Dark/Light/Auto),
// and Account (email, privacy, sign out, delete). Plus Reset Progress with a
// confirmation. Wires the existing soundService, ThemeContext and store.
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Screen from "../ui/Screen";
import { Toggle, SegmentedControl, Modal } from "../ui/primitives";
import { useTheme } from "../../contexts/ThemeContext";
import { loadUserProfile, saveUserProfile } from "../../services/storageService";
import { toggleSoundEffects, toggleMusic } from "../../services/soundService";
import { auth, signOut } from "../../services/firebaseService";
import { useElementStore } from "../../store/elementStore";
import { saveGameState } from "../../services/storageService";
import { triggerHaptic } from "../../services/capacitorService";

const SettingsScreen: React.FC = () => {
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const prefs = loadUserProfile().preferences;

  const [sound, setSound] = useState(prefs.soundEffects);
  const [music, setMusic] = useState(prefs.music);
  const [hints, setHints] = useState(prefs.showHints);
  const [confirmReset, setConfirmReset] = useState(false);

  const resetUnlocked = useElementStore((s) => s.resetUnlocked);
  const user = auth.currentUser;

  const savePrefs = (patch: Partial<typeof prefs>) => {
    const p = loadUserProfile();
    saveUserProfile({ preferences: { ...p.preferences, ...patch } });
  };

  const onSound = (v: boolean) => { setSound(v); toggleSoundEffects(v); savePrefs({ soundEffects: v }); };
  const onMusic = (v: boolean) => { setMusic(v); toggleMusic(v); savePrefs({ music: v }); };
  const onHints = (v: boolean) => {
    setHints(v);
    savePrefs({ showHints: v });
    try { window.dispatchEvent(new CustomEvent("mc-hints-changed", { detail: v })); } catch { /* noop */ }
  };

  const doReset = () => {
    triggerHaptic("medium");
    resetUnlocked();
    saveGameState({ gauntletProgress: 0, timeAttackHighScore: 0 });
    setConfirmReset(false);
  };

  const sectionLabel: React.CSSProperties = { fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--accent-text)", marginBottom: 10 };
  const card: React.CSSProperties = { borderRadius: 16, overflow: "hidden", background: "var(--surface)", border: "1px solid var(--border)" };
  const row: React.CSSProperties = { display: "flex", alignItems: "center", gap: 12, padding: "15px 16px", borderBottom: "1px solid var(--border)" };
  const accountRow: React.CSSProperties = { width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "15px 16px", border: "none", borderBottom: "1px solid var(--border)", background: "transparent", color: "var(--text)", fontFamily: "'DM Sans',sans-serif", fontSize: 14, fontWeight: 600, cursor: "pointer", textAlign: "left" };

  return (
    <Screen title="Settings & Account" showBack hideNav onBack={() => navigate(-1)}>
      <div className="mc-in" style={{ padding: "18px 16px", display: "flex", flexDirection: "column", gap: 20 }}>
        {/* Preferences */}
        <div>
          <div style={sectionLabel}>Preferences</div>
          <div style={card}>
            <div style={row}><span style={{ flex: 1, fontSize: 14, fontWeight: 600 }}>Sound effects</span><Toggle on={sound} onChange={onSound} label="Sound effects" /></div>
            <div style={row}><span style={{ flex: 1, fontSize: 14, fontWeight: 600 }}>Background music</span><Toggle on={music} onChange={onMusic} label="Background music" /></div>
            <div style={{ ...row, borderBottom: "none" }}><span style={{ flex: 1, fontSize: 14, fontWeight: 600 }}>Show hints</span><Toggle on={hints} onChange={onHints} label="Show hints" /></div>
          </div>
        </div>

        {/* Theme */}
        <div>
          <div style={sectionLabel}>Theme</div>
          <SegmentedControl
            ariaLabel="Theme"
            value={theme === "system" ? "auto" : theme}
            onChange={(v) => setTheme(v === "auto" ? "system" : (v as "dark" | "light"))}
            options={[{ value: "dark", label: "Dark" }, { value: "light", label: "Light" }, { value: "auto", label: "Auto" }]}
          />
        </div>

        {/* Account */}
        <div>
          <div style={sectionLabel}>Account</div>
          <div style={card}>
            <button className="mc-tap" style={accountRow} onClick={() => navigate("/auth")}>
              <div style={{ width: 36, height: 36, flex: "0 0 auto", borderRadius: "50%", background: "radial-gradient(circle at 34% 28%, var(--accent-text), #65A30D 60%, #3F6212)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, color: "#fff" }}>{(user?.email || "Y")[0].toUpperCase()}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 700 }}>{user ? "Signed in" : "Sign in / Sign up"}</div>
                <div style={{ fontSize: 12, color: "var(--text-2)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user?.email || "Not signed in"}</div>
              </div>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--text-3)" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg>
            </button>
            <button className="mc-tap" style={accountRow} onClick={() => navigate("/privacy-policy")}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#60A5FA" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
              <span style={{ flex: 1 }}>Privacy Policy</span>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--text-3)" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg>
            </button>
            {user && (
              <button className="mc-tap" style={accountRow} onClick={() => signOut().then(() => navigate("/"))}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--text-2)" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" /></svg>
                <span style={{ flex: 1 }}>Sign out</span>
              </button>
            )}
            <button className="mc-tap" style={{ ...accountRow, borderBottom: "none", color: "#F87171", fontWeight: 700 }} onClick={() => navigate("/delete-account")}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" /></svg>
              <span style={{ flex: 1 }}>Delete account</span>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#F87171" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg>
            </button>
          </div>
        </div>

        {/* Reset */}
        <button className="mc-tap" onClick={() => setConfirmReset(true)} style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 9, minHeight: 48, border: "1px solid rgba(244,114,182,0.35)", borderRadius: 14, background: "rgba(244,114,182,0.08)", color: "#F9A8D4", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 1 0 3-6.7L3 8m0-5v5h5" /></svg>
          Reset Progress
        </button>

        <div style={{ textAlign: "center", fontSize: 12, color: "var(--text-3)" }}>MystiCrafter · v2.0</div>
      </div>

      <Modal open={confirmReset} onClose={() => setConfirmReset(false)} title="Reset all progress?">
        <div style={{ fontSize: 14, color: "var(--text-2)", lineHeight: 1.5 }}>
          This clears every discovery, streak and mode progress back to the 10 base elements. This can't be undone.
        </div>
        <div style={{ marginTop: 18, display: "flex", gap: 10 }}>
          <button className="mc-tap" onClick={() => setConfirmReset(false)} style={{ flex: 1, minHeight: 46, border: "1px solid var(--border)", borderRadius: 12, background: "transparent", color: "var(--text-2)", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>Cancel</button>
          <button className="mc-tap" onClick={doReset} style={{ flex: 1, minHeight: 46, border: "none", borderRadius: 12, background: "#DC2626", color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>Reset</button>
        </div>
      </Modal>
    </Screen>
  );
};

export default SettingsScreen;
